import { useState, useRef } from 'react';
import { X, Upload, FileSpreadsheet, Download, CheckCircle, AlertCircle, Calendar, MessageSquare, Tag, Image as ImageIcon, FileEdit } from 'lucide-react';
import * as XLSX from 'xlsx';
import { adminThoughtsApi } from '../services/api';
import { toast } from 'sonner';

interface ExcelUploadProps {
  onClose: () => void;
  onImport: (thoughts: ImportedThought[]) => void;
}

export interface ImportedThought {
  date: string;
  text: string;
  category: string;
  imageUrl: string;
  author?: string;
  status: 'valid' | 'error';
  errorMessage?: string;
}

export default function ExcelUpload({ onClose, onImport }: ExcelUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [parsedData, setParsedData] = useState<ImportedThought[]>([]);
  const [_fileName, setFileName] = useState('');
  const [uploadStatus, setUploadStatus] = useState<'published' | 'scheduled' | 'draft'>('scheduled');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const processFile = async (file: File) => {
    // Validate file type
    const validTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv'
    ];
    
    if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv)$/)) {
      alert('Please upload a valid Excel file (.xlsx, .xls, or .csv)');
      return;
    }

    setFileName(file.name);
    setIsProcessing(true);

    try {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          
          // Get first sheet
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          
          // Convert to JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: false });
          
          // Parse and validate data
          const thoughts = parseExcelData(jsonData);
          setParsedData(thoughts);
          setIsProcessing(false);
        } catch (error) {
          console.error('Error parsing Excel:', error);
          alert('Error parsing Excel file. Please check the file format.');
          setIsProcessing(false);
        }
      };
      
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error('Error reading file:', error);
      alert('Error reading file. Please try again.');
      setIsProcessing(false);
    }
  };

  const parseExcelData = (data: any[]): ImportedThought[] => {
    return data.map((row) => {
      const thought: ImportedThought = {
        date: '',
        text: '',
        category: '',
        imageUrl: '',
        author: '',
        status: 'valid',
      };

      // Parse Date column
      const dateValue = row['Date'] || row['date'] || '';
      if (dateValue) {
        thought.date = parseDateValue(dateValue);
      } else {
        thought.status = 'error';
        thought.errorMessage = 'Missing date';
      }

      // Parse Thought column
      const thoughtText = row['Thought'] || row['thought'] || row['Quote'] || row['quote'] || '';
      if (thoughtText && thoughtText.trim()) {
        // Check for dedicated Author column first
        const dedicatedAuthor = (row['Author'] || row['author'] || '').toString().trim();
        if (dedicatedAuthor) {
          thought.text = thoughtText.replace(/^["""]|["""]$/g, '').trim();
          thought.author = dedicatedAuthor;
        } else {
          // Fallback: extract author from thought text if separated by ―
          const parts = thoughtText.split('―').map((p: string) => p.trim());
          if (parts.length === 2) {
            thought.text = parts[0].replace(/^["""]|["""]$/g, '').trim();
            thought.author = parts[1].trim();
          } else {
            thought.text = thoughtText.replace(/^["""]|["""]$/g, '').trim();
            thought.author = 'Anonymous';
          }
        }
      } else {
        thought.status = 'error';
        thought.errorMessage = 'Missing thought text';
      }

      // Parse Category
      const category = row['Category'] || row['category'] || '';
      if (category && category.trim()) {
        thought.category = category.trim();
      } else {
        thought.status = 'error';
        thought.errorMessage = thought.errorMessage ? `${thought.errorMessage}, Missing category` : 'Missing category';
      }

      // Parse Image URL
      const imageUrl = row['Image URL'] || row['ImageURL'] || row['image_url'] || row['imageUrl'] || '';
      thought.imageUrl = imageUrl.trim();

      // Additional validation
      if (thought.text && thought.text.length > 500) {
        thought.status = 'error';
        thought.errorMessage = 'Thought text too long (max 500 characters)';
      }

      return thought;
    });
  };

  const parseDateValue = (dateValue: any): string => {
    if (!dateValue) return '';

    if (typeof dateValue === 'string') {
      // "30-Apr-26" or "1-Dec-25" — parse manually to avoid timezone shifts
      const ddMmmYy = dateValue.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{2})$/);
      if (ddMmmYy) {
        const months: Record<string, string> = {
          jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06',
          jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12',
        };
        const day = ddMmmYy[1].padStart(2, '0');
        const month = months[ddMmmYy[2].toLowerCase()];
        const year = `20${ddMmmYy[3]}`;
        if (month) return `${year}-${month}-${day}`;
      }

      // "12/1/2025" — MM/DD/YYYY
      const mmDdYyyy = dateValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (mmDdYyyy) {
        const month = mmDdYyyy[1].padStart(2, '0');
        const day = mmDdYyyy[2].padStart(2, '0');
        return `${mmDdYyyy[3]}-${month}-${day}`;
      }

      // Already "2025-12-01"
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) return dateValue;

      return dateValue;
    }

    // Excel serial number — use UTC to avoid timezone shift
    if (typeof dateValue === 'number') {
      const excelEpoch = new Date(Date.UTC(1899, 11, 30));
      const date = new Date(excelEpoch.getTime() + dateValue * 86400000);
      const y = date.getUTCFullYear();
      const m = String(date.getUTCMonth() + 1).padStart(2, '0');
      const d = String(date.getUTCDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }

    return dateValue.toString();
  };

  const handleImport = async () => {
    const validThoughts = parsedData.filter(t => t.status === 'valid');
    if (validThoughts.length === 0) {
      toast.error('No valid thoughts to import. Please fix errors first.');
      return;
    }

    setIsUploading(true);
    
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Prepare thoughts for bulk upload
      const thoughtsForUpload = validThoughts.map(thought => {
        let status = uploadStatus;
        // Auto-determine status based on date only in "scheduled" mode
        // "Publish Now" forces published for all, "Draft" forces draft for all
        if (uploadStatus === 'scheduled' && thought.date) {
          const thoughtDate = new Date(thought.date + 'T00:00:00');
          status = thoughtDate <= today ? 'published' : 'scheduled';
        }
        return {
          text: thought.text,
          author: thought.author || 'Anonymous',
          category: thought.category,
          imageUrl: thought.imageUrl || '',
          date: thought.date || undefined,
          status,
        };
      });

      console.log('📤 Uploading thoughts:', thoughtsForUpload);

      // Call the bulk upload API
      const result = await adminThoughtsApi.bulkUpload(thoughtsForUpload);
      
      console.log('📥 API Response:', result);
      
      if (result.success) {
        // Create success message with category info
        let successMessage = `Successfully imported ${result.created || validThoughts.length} thoughts!`;
        
        if (result.categoriesCreated && result.categoriesCreated > 0) {
          successMessage += ` 🏷️ Created ${result.categoriesCreated} new categor${result.categoriesCreated === 1 ? 'y' : 'ies'}: ${result.newCategories.join(', ')}`;
        }
        
        toast.success(successMessage, { duration: 5000 });
        onImport(validThoughts);
        
        // Wait 2 seconds to show success message before closing
        setTimeout(() => {
          onClose();
          // Refresh the page to show new thoughts
          window.location.reload();
        }, 2000);
      } else {
        console.error('❌ Import failed:', result);
        toast.error(result.message || 'Failed to import thoughts. Please try again.');
        setIsUploading(false);
      }
    } catch (error: any) {
      console.error('❌ Error importing thoughts:', error);
      
      // Show detailed error message
      const errorMessage = error?.response?.data?.message 
        || error?.message 
        || 'Failed to import thoughts. Please check console for details.';
      
      toast.error(errorMessage);
      
      // Log full error details
      if (error?.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }
      
      setIsUploading(false);
    }
  };

  const downloadTemplate = () => {
    // Create sample data
    const template = [
      {
        'Date': '1-Dec-25',
        'Thought': 'Anti-social behavior is a trait of intelligence in a world full of conformists',
        'Author': 'Nikola Tesla',
        'Category': 'Wisdom',
        'Image URL': 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4'
      },
      {
        'Date': '2-Dec-25',
        'Thought': 'The only way to do great work is to love what you do',
        'Author': 'Steve Jobs',
        'Category': 'Motivation',
        'Image URL': 'https://images.unsplash.com/photo-1469474968028-56623f02e42e'
      },
      {
        'Date': '3-Dec-25',
        'Thought': 'Be yourself; everyone else is already taken',
        'Author': 'Oscar Wilde',
        'Category': 'Self',
        'Image URL': ''
      }
    ];

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Thoughts');

    // Set column widths
    ws['!cols'] = [
      { wch: 12 },  // Date
      { wch: 70 },  // Thought
      { wch: 20 },  // Author
      { wch: 15 },  // Category
      { wch: 50 }   // Image URL
    ];

    // Download
    XLSX.writeFile(wb, 'TOTD_Template.xlsx');
  };

  const validCount = parsedData.filter(t => t.status === 'valid').length;
  const errorCount = parsedData.filter(t => t.status === 'error').length;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#2B7A7E] to-[#1e5a5d] text-white px-8 py-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Bulk Import Thoughts</h2>
            <p className="text-white/80 text-sm mt-1">
              Upload an Excel file to import multiple thoughts at once
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8">
          {parsedData.length === 0 ? (
            <>
              {/* Upload Area */}
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`relative border-2 border-dashed rounded-2xl p-12 transition-all ${
                  isDragging
                    ? 'border-[#2B7A7E] bg-[#2B7A7E]/5'
                    : 'border-slate-300 hover:border-[#2B7A7E] hover:bg-slate-50'
                }`}
              >
                <div className="flex flex-col items-center justify-center text-center">
                  <div className="w-20 h-20 bg-[#2B7A7E]/10 rounded-full flex items-center justify-center mb-6">
                    <FileSpreadsheet className="w-10 h-10 text-[#2B7A7E]" />
                  </div>
                  
                  <h3 className="text-xl font-bold text-slate-800 mb-2">
                    {isProcessing ? 'Processing file...' : 'Drop your Excel file here'}
                  </h3>
                  <p className="text-slate-500 mb-6">
                    or click to browse your computer
                  </p>

                  {!isProcessing && (
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="px-6 py-3 bg-gradient-to-r from-[#2B7A7E] to-[#1e5a5d] text-white rounded-xl font-semibold hover:shadow-lg transition-all flex items-center gap-2"
                    >
                      <Upload className="w-5 h-5" />
                      Choose File
                    </button>
                  )}

                  {isProcessing && (
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 border-2 border-[#2B7A7E] border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-slate-600">Processing your file...</span>
                    </div>
                  )}

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              </div>

              {/* Template Download */}
              <div className="mt-8 p-6 bg-blue-50 border-2 border-blue-200 rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Download className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-blue-900 mb-2">
                      Need a template?
                    </h4>
                    <p className="text-sm text-blue-800 mb-4">
                      Download our Excel template with sample data and correct column format
                    </p>
                    <button
                      onClick={downloadTemplate}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download Template
                    </button>
                  </div>
                </div>
              </div>

              {/* Format Instructions */}
              <div className="mt-6 p-6 bg-amber-50 border-2 border-amber-200 rounded-xl">
                <h4 className="font-bold text-amber-900 mb-3">
                  📋 Required Excel Format
                </h4>
                <div className="space-y-2 text-sm text-amber-800">
                  <p><strong>Column 1:</strong> Date (e.g., "1-Dec-25" or "12/1/2025")</p>
                  <p><strong>Column 2:</strong> Thought (The quote text only)</p>
                  <p><strong>Column 3:</strong> Author (e.g., "Steve Jobs", "Rumi" — leave blank for Anonymous)</p>
                  <p><strong>Column 4:</strong> Category (e.g., "Wisdom", "Motivation", "Life")</p>
                  <p><strong>Column 5:</strong> Image URL (Optional - can be left blank)</p>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-slate-50 rounded-xl border-2 border-slate-200">
                  <div className="flex items-center gap-3">
                    <FileSpreadsheet className="w-8 h-8 text-slate-600" />
                    <div>
                      <p className="text-sm text-slate-600">Total Rows</p>
                      <p className="text-2xl font-bold text-slate-900">{parsedData.length}</p>
                    </div>
                  </div>
                </div>
                <div className="p-4 bg-green-50 rounded-xl border-2 border-green-200">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                    <div>
                      <p className="text-sm text-green-700">Valid Thoughts</p>
                      <p className="text-2xl font-bold text-green-900">{validCount}</p>
                    </div>
                  </div>
                </div>
                <div className="p-4 bg-red-50 rounded-xl border-2 border-red-200">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-8 h-8 text-red-600" />
                    <div>
                      <p className="text-sm text-red-700">Errors</p>
                      <p className="text-2xl font-bold text-red-900">{errorCount}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Status Selector */}
              <div className="mb-6 p-6 bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-xl">
                <div className="mb-4">
                  <h4 className="font-bold text-purple-900 mb-1">Publication Status</h4>
                  <p className="text-sm text-purple-700">
                    Choose how to import these thoughts
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setUploadStatus('published')}
                    className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
                      uploadStatus === 'published'
                        ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg scale-105'
                        : 'bg-white text-slate-700 border-2 border-slate-300 hover:border-green-500'
                    }`}
                  >
                    <CheckCircle className="w-5 h-5" />
                    Publish Now
                  </button>
                  <button
                    onClick={() => setUploadStatus('scheduled')}
                    className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
                      uploadStatus === 'scheduled'
                        ? 'bg-gradient-to-r from-[#2B7A7E] to-[#1e5a5d] text-white shadow-lg scale-105'
                        : 'bg-white text-slate-700 border-2 border-slate-300 hover:border-[#2B7A7E]'
                    }`}
                  >
                    <Calendar className="w-5 h-5" />
                    Schedule
                  </button>
                  <button
                    onClick={() => setUploadStatus('draft')}
                    className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
                      uploadStatus === 'draft'
                        ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-lg scale-105'
                        : 'bg-white text-slate-700 border-2 border-slate-300 hover:border-amber-500'
                    }`}
                  >
                    <FileEdit className="w-5 h-5" />
                    Draft
                  </button>
                </div>
                <div className="mt-4 p-3 bg-white/70 rounded-lg">
                  <p className="text-sm text-slate-700">
                    {uploadStatus === 'published' ? (
                      <span>✅ <strong>Publish Now:</strong> All thoughts will be immediately visible to users</span>
                    ) : uploadStatus === 'scheduled' ? (
                      <span>📅 <strong>Schedule:</strong> Past/today's thoughts → Published automatically. Future thoughts → Scheduled by their date</span>
                    ) : (
                      <span>📝 <strong>Draft:</strong> Thoughts will be saved as drafts for review and editing before publishing</span>
                    )}
                  </p>
                </div>
              </div>

              {/* Data Preview Table */}
              <div className="border-2 border-slate-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 w-12">#</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 w-28">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            Date
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">
                          <div className="flex items-center gap-2">
                            <MessageSquare className="w-4 h-4" />
                            Thought & Author
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 w-32">
                          <div className="flex items-center gap-2">
                            <Tag className="w-4 h-4" />
                            Category
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 w-20">
                          <div className="flex items-center gap-2">
                            <ImageIcon className="w-4 h-4" />
                            Image
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 w-24">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {parsedData.map((thought, index) => (
                        <tr
                          key={index}
                          className={thought.status === 'error' ? 'bg-red-50' : 'hover:bg-slate-50'}
                        >
                          <td className="px-4 py-3 text-sm text-slate-600">{index + 1}</td>
                          <td className="px-4 py-3 text-sm text-slate-900">{thought.date}</td>
                          <td className="px-4 py-3">
                            <div className="text-sm">
                              <p className="text-slate-900 line-clamp-2">{thought.text}</p>
                              <p className="text-slate-500 text-xs mt-1">― {thought.author}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-block px-2 py-1 bg-[#2B7A7E]/10 text-[#2B7A7E] rounded text-xs font-medium">
                              {thought.category}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {thought.imageUrl ? (
                              <CheckCircle className="w-5 h-5 text-green-600" />
                            ) : (
                              <X className="w-5 h-5 text-slate-400" />
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {thought.status === 'valid' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                                <CheckCircle className="w-3 h-3" />
                                Valid
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">
                                <AlertCircle className="w-3 h-3" />
                                Error
                              </span>
                            )}
                            {thought.errorMessage && (
                              <p className="text-xs text-red-600 mt-1">{thought.errorMessage}</p>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {parsedData.length > 0 && (
          <div className="border-t border-slate-200 px-8 py-6 bg-slate-50">
            <div className="flex items-center justify-between gap-4">
              <div className="text-sm text-slate-600">
                <p className="font-semibold">
                  Ready to import {validCount} thought{validCount !== 1 ? 's' : ''} as{' '}
                  {uploadStatus === 'published' ? '✅ Published' : uploadStatus === 'scheduled' ? '📅 Scheduled' : '📝 Draft'}
                </p>
                {errorCount > 0 && (
                  <p className="text-red-600">
                    {errorCount} row{errorCount !== 1 ? 's' : ''} with errors will be skipped
                  </p>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setParsedData([])}
                  className="px-6 py-3 border-2 border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors font-medium"
                >
                  Upload Different File
                </button>
                <button
                  onClick={handleImport}
                  disabled={validCount === 0 || isUploading}
                  className="px-8 py-3 bg-gradient-to-r from-[#2B7A7E] to-[#1e5a5d] text-white rounded-lg hover:shadow-lg transition-all font-semibold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUploading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      Import {validCount} Thought{validCount !== 1 ? 's' : ''}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}